/**
 * API klient — tenký wrapper nad fetch s credentials + JSON.
 */
import type {
  ArticleDetail,
  CategoriesResponse,
  ModelDetail,
  ModelsListResponse,
  PowerpartsCategory,
  PowerpartsListResponse,
  SubgroupPartsResponse,
  PimListResponse,
  PimPowerwearListResponse,
  PimCollectionsResponse,
  ModelPowerpartsResponse,
  ModelAssetsResponse,
  MefoListResponse,
  MefoManufacturersResponse,
  MefoProductDetail,
  MotorexListResponse,
  MotorexCategoriesResponse,
  MotorexStatsResponse,
  MotorexProductDetail,
  MotorexAbraBezProtejskuResponse,
  RiskListResponse,
  RiskTypesResponse,
  RiskStatsResponse,
  RiskProductDetail,
  GarminListResponse,
  GarminCategoriesResponse,
  GarminProductDetail,
} from "./types.js";

const BASE = "/api";

/**
 * Přečte preferovaný locale z localStorage (settings kontext).
 * Fallback na 'cs' pokud settings ještě nebyl inicializován.
 */
function currentLocale(): string {
  try {
    const raw = localStorage.getItem("ktm.settings");
    if (!raw) return "cs";
    const parsed = JSON.parse(raw) as { locale?: string };
    return parsed.locale ?? "cs";
  } catch {
    return "cs";
  }
}

async function fetchJson<T>(path: string): Promise<T> {
  const locale = currentLocale();
  // Přidáme `?locale=` do všech API volání (priorita nad Accept-Language header).
  const sep = path.includes("?") ? "&" : "?";
  const url = path.includes("locale=") ? path : `${path}${sep}locale=${locale}`;
  const res = await fetch(`${BASE}${url}`, {
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Accept-Language": locale,
    },
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}: ${await res.text().catch(() => "")}`);
  }
  return res.json() as Promise<T>;
}

export interface SearchModelsParams {
  search?: string | undefined;
  brand?: string | undefined;
  region?: string | undefined;
  category?: string | undefined;
  year?: number | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

export async function searchModels(
  params: SearchModelsParams,
): Promise<ModelsListResponse> {
  const qs = new URLSearchParams();
  if (params.search) qs.set("search", params.search);
  if (params.brand) qs.set("brand", params.brand);
  if (params.region) qs.set("region", params.region);
  if (params.category) qs.set("category", params.category);
  if (params.year !== undefined) qs.set("year", String(params.year));
  qs.set("limit", String(params.limit ?? 30));
  qs.set("offset", String(params.offset ?? 0));
  qs.set("onlyLeaves", "true");
  return fetchJson(`/catalog/models?${qs}`);
}

export async function getCategories(
  brand = "ktm",
  region = "EU",
): Promise<CategoriesResponse> {
  const qs = new URLSearchParams({ brand, region });
  return fetchJson<CategoriesResponse>(`/catalog/categories?${qs}`);
}

export async function getModelDetail(id: string): Promise<ModelDetail> {
  return fetchJson<ModelDetail>(`/catalog/models/${id}`);
}

export async function getSubgroupParts(
  subgroupId: string,
  modelId: string,
): Promise<SubgroupPartsResponse> {
  const qs = new URLSearchParams({ modelId });
  return fetchJson<SubgroupPartsResponse>(
    `/catalog/subgroups/${subgroupId}/parts?${qs}`,
  );
}

export async function getArticleDetail(articleNumber: string): Promise<ArticleDetail> {
  return fetchJson<ArticleDetail>(`/catalog/articles/${encodeURIComponent(articleNumber)}`);
}

export async function getPowerpartsCategories(): Promise<{ categories: PowerpartsCategory[] }> {
  return fetchJson(`/catalog/powerparts/categories`);
}

export interface PowerpartsListParams {
  l2?: string | undefined;
  l3?: string | undefined;
  search?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
  hasStock?: boolean | undefined;
  includeDiscontinued?: boolean | undefined;
}

export async function listPowerparts(
  params: PowerpartsListParams = {},
): Promise<PowerpartsListResponse> {
  const qs = new URLSearchParams();
  if (params.l2) qs.set("l2", params.l2);
  if (params.l3) qs.set("l3", params.l3);
  if (params.search) qs.set("search", params.search);
  if (params.hasStock) qs.set("hasStock", "true");
  if (params.includeDiscontinued) qs.set("includeDiscontinued", "true");
  qs.set("limit", String(params.limit ?? 48));
  qs.set("offset", String(params.offset ?? 0));
  return fetchJson(`/catalog/powerparts?${qs}`);
}

export async function getMe(): Promise<{
  authEnabled: boolean;
  user: { id: string; role: string; name?: string } | null;
}> {
  const res = await fetch(`${BASE}/auth/me`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  // 401 = AUTH_ENABLED=true, ale chybí session → ukázat LoginPage
  if (res.status === 401) {
    return { authEnabled: true, user: null };
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<{
    authEnabled: boolean;
    user: { id: string; role: string; name?: string } | null;
  }>;
}

/** POST /api/auth/login — přihlásí uživatele. Vrací { ok, user }. */
export async function loginWithCredentials(
  email: string,
  password: string,
): Promise<{ ok: true; user: { id: string; email: string; role: string; name?: string } }> {
  const res = await fetch(`${BASE}/auth/login`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    const code = body.error ?? `HTTP ${res.status}`;
    throw new Error(code);
  }
  return res.json() as Promise<{ ok: true; user: { id: string; email: string; role: string; name?: string } }>;
}

/** POST /api/auth/logout — zruší session a smaže cookies. */
export async function logoutSession(): Promise<void> {
  await fetch(`${BASE}/auth/logout`, {
    method: "POST",
    credentials: "include",
  }).catch(() => undefined);
}

// =============================================================================
// Admin: integrations
// =============================================================================

export interface IntegrationConfigKey {
  key: string;
  set: boolean;
}

export interface IntegrationLastRun {
  at: string;
  status: string;
  summary: string;
}

export interface Integration {
  id: string;
  label: string;
  kind: "api" | "ftp" | "importer" | "warehouse" | "export" | "translation";
  description: string;
  status: "active" | "configured" | "unconfigured";
  configKeys: IntegrationConfigKey[];
  lastRun: IntegrationLastRun | null;
  detailRoute: string | null;
}

export async function listIntegrations(): Promise<{ integrations: Integration[] }> {
  return fetchJson(`/admin/integrations`);
}

// =============================================================================
// Admin: automations (naši roboti — stav z automatizace_status.py)
// =============================================================================

export interface AutomationRobot {
  id: string;
  label: string;
  plan: string;
  popis: string;
  lastRun: string | null;
  ok: boolean | null;
  note: string;
  log: string;
  // ⚠ Nepovinné: sběrač na serveru se nasazuje zvlášť od téhle appky, takže
  //    chvíli po deployi můžou pole chybět. Stránka na to musí být zvyklá.
  lastRunUtc?: string | null; // čas v UTC — z něj se počítá „před 15 h"
  skupina?: string; // blok: ceny · eshop · vyrobce · hlidaci · obsah · provoz
  druh?: "robot" | "hlidac"; // hlídač končí kódem 1 ZÁMĚRNĚ = nález, ne porucha
  co?: string; // jedna věta na kartu (`popis` zůstává plnou pravdou)
  cil?: string; // kam zapisuje: eshop · katalog · hlasi · zaloha · provoz
  zkratka?: string; // krátký název do dlaždice nahoře
  mezH?: number | null; // po kolika hodinách je „dlouho neběžel"
  poradi?: number;
  // 🆕 17. 8. 2026 — pilulka „Audit + datum": kdy robot prošel detailním
  // auditem (Vít je projíždí jeden po druhém). Bez pole = ještě nerozpitváno.
  audit?: string | null; // ISO datum posledního auditu, např. "2026-08-17"
  auditDoc?: string | null; // kanonický dokument auditu v repu ktm-vlasim
  // 🆕 17. 8. 2026 — ZNAČKA (přání Víta „chci vidět, co je KTM a co ostatní").
  // Skupiny zůstávají podle PRÁCE, značka je jen filtr přes ně.
  // ktm · acerbis · b2bike · motorex · risk · vse (napříč značkami)
  znacka?: string;
}

export interface AutomationsResponse {
  generatedAt: string | null;
  ageMinutes: number | null;
  stale: boolean;
  missing: boolean;
  roboti: AutomationRobot[];
  /** Číselník značek ze sběrače (id + lidský název). Starší sběrač ho nemá. */
  znacky?: { id: string; nazev: string }[];
}

export async function listAutomations(): Promise<AutomationsResponse> {
  return fetchJson(`/admin/automations`);
}

// =============================================================================
// Admin: customers
// =============================================================================

export interface CustomerListItem {
  id: string;
  legacyId: number | null;
  email: string;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  ico: string | null;
  city: string | null;
  country: string | null;
  phone: string | null;
  registeredAt: string;
  isB2B: boolean;
  hasShopify: boolean;
  discountCount: number;
}

export interface CustomerDiscount {
  id: string;
  category: string;
  percent: number;
}

export interface CustomerDetail {
  id: string;
  legacyId: number | null;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  ico: string | null;
  dic: string | null;
  companyName: string | null;
  street: string | null;
  city: string | null;
  zip: string | null;
  country: string | null;
  rawAddress: string | null;
  shopifyCustomerId: string | null;
  shopifySyncedAt: string | null;
  notes: string | null;
  registeredAt: string;
  createdAt: string;
  updatedAt: string;
  importedAt: string | null;
  importSource: string | null;
  isB2B: boolean;
  discounts: CustomerDiscount[];
}

export interface CustomersListResponse {
  total: number;
  counts: { all: number; b2b: number; b2c: number };
  items: CustomerListItem[];
}

export async function listCustomers(params: {
  search?: string;
  type?: "all" | "b2b" | "b2c";
  limit?: number;
  offset?: number;
}): Promise<CustomersListResponse> {
  const qs = new URLSearchParams();
  if (params.search) qs.set("search", params.search);
  if (params.type) qs.set("type", params.type);
  qs.set("limit", String(params.limit ?? 50));
  qs.set("offset", String(params.offset ?? 0));
  return fetchJson(`/admin/customers?${qs}`);
}

export async function getCustomer(id: string): Promise<CustomerDetail> {
  return fetchJson(`/admin/customers/${id}`);
}

export async function patchCustomer(
  id: string,
  data: Partial<Omit<CustomerDetail, "id" | "discounts" | "registeredAt" | "createdAt" | "updatedAt" | "importedAt" | "importSource" | "shopifyCustomerId" | "shopifySyncedAt" | "rawAddress" | "legacyId" | "isB2B">>,
): Promise<{ ok: true; customer: CustomerDetail }> {
  const res = await fetch(`${BASE}/admin/customers/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text().catch(() => "")}`);
  return res.json() as Promise<{ ok: true; customer: CustomerDetail }>;
}

export async function putCustomerDiscounts(
  id: string,
  discounts: Array<{ category: string; percent: number }>,
): Promise<{ ok: true; count: number }> {
  const res = await fetch(`${BASE}/admin/customers/${id}/discounts`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ discounts }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text().catch(() => "")}`);
  return res.json() as Promise<{ ok: true; count: number }>;
}

// =============================================================================
// B2Bike
// =============================================================================

export interface B2bikeListItem {
  id: string;
  b2bikeId: number;
  code: string;
  name: string | null;
  producer: string | null;
  category: string | null;
  priceGross: string | null;
  priceNet: string | null;
  currency: string;
  imageUrl: string | null;
  variantsTotal: number;
  variantsInStock: number;
  totalStock: number | "unlimited";
  hasShopify: boolean;
}

export interface B2bikeVariant {
  id: string;
  sizeName: string;
  sizeCode: string;
  ean: string | null;
  externalCode: string | null;
  weightGrams: number | null;
  priceGross: string | null;
  priceNet: string | null;
  srpGross: string | null;
  stockQuantity: number | null;
  inStock: boolean;
  isActive: boolean;
}

export interface B2bikeImage {
  id: string;
  url: string;
  width: number | null;
  height: number | null;
  size: string;
  isPrimary: boolean;
}

export interface B2bikeDetail {
  id: string;
  b2bikeId: number;
  code: string;
  type: string;
  vatRate: string | null;
  currency: string;
  producer: { id: number | null; name: string | null };
  category: { id: number | null; name: string | null };
  cardUrl: string | null;
  nameCz: string | null;
  nameEn: string | null;
  shortDescCz: string | null;
  shortDescEn: string | null;
  longDescCz: string | null;
  longDescEn: string | null;
  priceGross: string | null;
  priceNet: string | null;
  srpGross: string | null;
  srpNet: string | null;
  omnibusRetailGross: string | null;
  isActive: boolean;
  lastSeenAt: string;
  shopifyProductId: string | null;
  variants: B2bikeVariant[];
  images: B2bikeImage[];
}

export async function listB2bike(params: {
  search?: string;
  producer?: number;
  category?: number;
  inStock?: boolean;
  limit?: number;
  offset?: number;
}): Promise<{ total: number; items: B2bikeListItem[] }> {
  const qs = new URLSearchParams();
  if (params.search) qs.set("search", params.search);
  if (params.producer !== undefined) qs.set("producer", String(params.producer));
  if (params.category !== undefined) qs.set("category", String(params.category));
  if (params.inStock) qs.set("inStock", "true");
  qs.set("limit", String(params.limit ?? 30));
  qs.set("offset", String(params.offset ?? 0));
  return fetchJson(`/catalog/b2bike?${qs}`);
}

export async function listB2bikeProducers(): Promise<{
  items: Array<{ id: number | null; name: string | null; count: number }>;
}> {
  return fetchJson(`/catalog/b2bike/producers`);
}

export async function getB2bikeDetail(id: string): Promise<B2bikeDetail> {
  return fetchJson(`/catalog/b2bike/${id}`);
}

export interface B2bikeStatus {
  counts: {
    productsTotal: number;
    productsActive: number;
    variantsTotal: number;
    variantsInStock: number;
    imagesCount: number;
  };
  recentRuns: Array<{
    id: string;
    kind: string;
    status: string;
    startedAt: string;
    finishedAt: string | null;
    productsSeen: number;
    productsNew: number;
    productsUpdated: number;
    productsVanished: number;
    imagesDownloaded: number;
  }>;
}

export async function getB2bikeStatus(): Promise<B2bikeStatus> {
  return fetchJson(`/admin/b2bike/status`);
}

export async function triggerB2bikeSync(
  kind: "reference" | "full" | "light",
  opts: { skipImages?: boolean; maxProducts?: number } = {},
): Promise<{ ok: true; kind: string }> {
  const res = await fetch(`${BASE}/admin/b2bike/sync`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ kind, ...opts }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text().catch(() => "")}`);
  return res.json() as Promise<{ ok: true; kind: string }>;
}

// =============================================================================
// PIM catalog — moderní endpointy nad PIM daty (/api/catalog/pim/*)
// =============================================================================

export interface PimListParams {
  search?: string | undefined;
  category?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
  hasStock?: boolean | undefined;
  includeDiscontinued?: boolean | undefined;
}

export async function listPimPowerparts(
  params: PimListParams = {},
): Promise<PimListResponse> {
  const qs = buildQs(params as Record<string, string | number | boolean | undefined>);
  return fetchJson(`/catalog/pim/powerparts?${qs}`);
}

export interface PimPowerwearParams {
  search?: string | undefined;
  size?: string | undefined;
  colour?: string | undefined;
  gender?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
  hasStock?: boolean | undefined;
  includeDiscontinued?: boolean | undefined;
}

export async function listPimPowerwear(
  params: PimPowerwearParams = {},
): Promise<PimPowerwearListResponse> {
  const qs = buildQs(params as Record<string, string | number | boolean | undefined>);
  return fetchJson(`/catalog/pim/powerwear?${qs}`);
}

export interface PimCollectionsParams {
  packageType?: string | undefined;
  search?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
  includeDiscontinued?: boolean | undefined;
}

export async function listPimCollections(
  params: PimCollectionsParams = {},
): Promise<PimCollectionsResponse> {
  const qs = buildQs(params as Record<string, string | number | boolean | undefined>);
  return fetchJson(`/catalog/pim/collections?${qs}`);
}

/** Kompatibilní PowerParts / PowerWear / Kits pro daný model. */
export async function getModelPowerparts(
  modelId: string,
  category?: string,
): Promise<ModelPowerpartsResponse> {
  const qs = category ? `?category=${encodeURIComponent(category)}` : "";
  return fetchJson(`/catalog/models/${encodeURIComponent(modelId)}/powerparts${qs}`);
}

/** Dokumenty (PDF) a marketingové obrázky přiřazené k modelu z PIM. */
export async function getModelAssets(
  modelId: string,
): Promise<ModelAssetsResponse> {
  return fetchJson(`/catalog/models/${encodeURIComponent(modelId)}/assets`);
}

// ─── MEFO ────────────────────────────────────────────────────────────────────

export interface MefoListParams {
  manufacturer?: string | undefined;
  skupina?: string | undefined;
  search?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
  showVanished?: boolean | undefined;
}

export async function listMefo(params: MefoListParams = {}): Promise<MefoListResponse> {
  const qs = buildQs(params as Record<string, string | number | boolean | undefined>);
  return fetchJson(`/catalog/mefo?${qs}`);
}

export async function getMefoManufacturers(): Promise<MefoManufacturersResponse> {
  return fetchJson(`/catalog/mefo/manufacturers`);
}

export async function getMefoDetail(itemId: string): Promise<MefoProductDetail> {
  return fetchJson(`/catalog/mefo/${encodeURIComponent(itemId)}`);
}

// ─── MOTOREX ─────────────────────────────────────────────────────────────────

export interface MotorexListParams {
  category?: string | undefined;
  scope?: string | undefined;
  /** `vedeme` · `nevedeme` · `neurceno` — párováno přes kód výrobce na ABRU. */
  stocked?: string | undefined;
  search?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
  showVanished?: boolean | undefined;
  /** Stav vůči eshopu: `na-eshopu` · `skryto` · `mimo`. */
  eshop?: string | undefined;
}

export async function listMotorex(
  params: MotorexListParams = {},
): Promise<MotorexListResponse> {
  const qs = buildQs(params as Record<string, string | number | boolean | undefined>);
  return fetchJson(`/catalog/motorex?${qs}`);
}

export async function getMotorexCategories(): Promise<MotorexCategoriesResponse> {
  return fetchJson(`/catalog/motorex/categories`);
}

export async function getMotorexStats(): Promise<MotorexStatsResponse> {
  return fetchJson(`/catalog/motorex/stats`);
}

export async function getMotorexDetail(itemId: string): Promise<MotorexProductDetail> {
  return fetchJson(`/catalog/motorex/${encodeURIComponent(itemId)}`);
}

/** Co vedeme, ale výrobce to ve feedu nemá (sudy, cizí zboží, staré karty). */
export async function getMotorexAbraBezProtejsku(): Promise<MotorexAbraBezProtejskuResponse> {
  return fetchJson(`/catalog/motorex/abra/bez-protejsku`);
}

/**
 * Rozhodnout o NOVÉ položce z „DOPORUČENÉ PRODUKTY". Jen admin.
 * ⚠ Rozsah se drží v kódu (`scope.ts`), takže tohle rozhodnutí jen zapíše —
 * provede ho až člověk, který ho tam přenese. Odpověď to říká přes
 * `zaneseDoKoduRucne`.
 */
export async function setMotorexRozhodnuti(
  itemId: string,
  patriKNam: boolean,
): Promise<{ ok: true; itemId: string; patriKNam: boolean; zaneseDoKoduRucne: boolean }> {
  const res = await fetch(
    `${BASE}/catalog/motorex/${encodeURIComponent(itemId)}/rozhodnuti`,
    {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ patriKNam }),
    },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text().catch(() => "")}`);
  return res.json() as Promise<{
    ok: true;
    itemId: string;
    patriKNam: boolean;
    zaneseDoKoduRucne: boolean;
  }>;
}

// ─── Garmin ──────────────────────────────────────────────────────────────────

export interface GarminListParams {
  category?: string | undefined;
  productType?: string | undefined;
  search?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
  inStock?: boolean | undefined;
  showVanished?: boolean | undefined;
}

export async function listGarmin(params: GarminListParams = {}): Promise<GarminListResponse> {
  const qs = buildQs(params as Record<string, string | number | boolean | undefined>);
  return fetchJson(`/catalog/garmin?${qs}`);
}

export async function getGarminCategories(): Promise<GarminCategoriesResponse> {
  return fetchJson(`/catalog/garmin/categories`);
}

export async function getGarminDetail(code: string): Promise<GarminProductDetail> {
  return fetchJson(`/catalog/garmin/${encodeURIComponent(code)}`);
}

/** Helper — složí URLSearchParams z záznamu, null/undefined/false ignoruje. */
function buildQs(obj: Record<string, string | number | boolean | undefined>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null || v === false || v === "") continue;
    qs.set(k, String(v));
  }
  if (!qs.has("limit")) qs.set("limit", "48");
  if (!qs.has("offset")) qs.set("offset", "0");
  return qs.toString();
}

export interface CreateQuoteBody {
  customerName?: string | undefined;
  customerEmail?: string | undefined;
  customerPhone?: string | undefined;
  note?: string | undefined;
  items: Array<{
    articleNumber: string;
    articleName?: string | null | undefined;
    quantity: number;
    unitPriceNetCzk: number | string;
    vatRate?: number | undefined;
    modelId?: string | null | undefined;
    componentSubgroupId?: string | null | undefined;
    lineNumber?: number | null | undefined;
  }>;
}

export async function createQuoteApi(body: CreateQuoteBody): Promise<{ id: string }> {
  const res = await fetch(`${BASE}/quotes`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json();
}

export interface QuoteDetail {
  id: string;
  number: string;
  customerName: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  note: string | null;
  status: string;
  subtotalNetCzk: string;
  vatCzk: string;
  totalGrossCzk: string;
  createdAt: string;
  items: Array<{
    id: number;
    position: number;
    articleNumber: string;
    articleName: string | null;
    quantity: number;
    unitPriceNetCzk: string;
    vatRate: number;
    lineNumber: number | null;
    model: {
      id: string;
      description: string | null;
      year: number | null;
      articleNumber: string | null;
    } | null;
    subgroup: {
      id: string;
      code: string;
      description: string;
      imageUrl: string | null;
    } | null;
  }>;
}

export async function getQuoteApi(id: string): Promise<QuoteDetail> {
  return fetchJson<QuoteDetail>(`/quotes/${id}`);
}

export function quotePdfUrl(id: string): string {
  return `${BASE}/quotes/${id}/pdf`;
}

export async function listQuotesApi(): Promise<{
  total: number;
  items: Array<{
    id: string;
    number: string;
    customerName: string | null;
    status: string;
    totalGrossCzk: string;
    itemCount: number;
    createdAt: string;
  }>;
}> {
  return fetchJson(`/quotes?limit=100`);
}

// ─── RISK RACING ─────────────────────────────────────────────────────────────

export interface RiskListParams {
  type?: string | undefined;
  /** `vedeme` · `nevedeme` — podle toho, jestli má kartu v ABŘE. */
  stocked?: string | undefined;
  search?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
  showVanished?: boolean | undefined;
}

export async function listRisk(params: RiskListParams = {}): Promise<RiskListResponse> {
  const qs = buildQs(params as Record<string, string | number | boolean | undefined>);
  return fetchJson(`/catalog/risk?${qs}`);
}

export async function getRiskTypes(): Promise<RiskTypesResponse> {
  return fetchJson(`/catalog/risk/types`);
}

export async function getRiskStats(): Promise<RiskStatsResponse> {
  return fetchJson(`/catalog/risk/stats`);
}

export async function getRiskDetail(sku: string): Promise<RiskProductDetail> {
  return fetchJson(`/catalog/risk/${encodeURIComponent(sku)}`);
}

/**
 * Ovládání eshopu z katalogu.
 *
 * ⛔ „Schválit" produkt NEZAKLÁDÁ — jen zapíše příznak. Zakládá robot
 * `risk_zalozeni.py` (repo `ktm-vlasim`), který jede každých 15 minut.
 * „Skrýt" naopak dělá katalog hned, je to jedno pole (`status: DRAFT`).
 */
async function riskAkce(
  sku: string,
  akce: "schvalit" | "zrusit-schvaleni" | "skryt",
): Promise<void> {
  const res = await fetch(`${BASE}/catalog/risk/${encodeURIComponent(sku)}/${akce}`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    // `detail` nese větu pro člověka (kolize kódů), `error` strojový kód.
    throw new Error(body.detail ?? body.error ?? `HTTP ${res.status}`);
  }
}

async function motorexAkce(
  itemId: string,
  akce: "schvalit" | "zrusit-schvaleni" | "skryt",
): Promise<void> {
  const res = await fetch(`${BASE}/catalog/motorex/${encodeURIComponent(itemId)}/${akce}`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    throw new Error(body.detail ?? body.error ?? `HTTP ${res.status}`);
  }
}

export const schvalitMotorex = (id: string): Promise<void> => motorexAkce(id, "schvalit");
export const zrusitSchvaleniMotorex = (id: string): Promise<void> =>
  motorexAkce(id, "zrusit-schvaleni");
export const skrytMotorex = (id: string): Promise<void> => motorexAkce(id, "skryt");

export const schvalitRisk = (sku: string): Promise<void> => riskAkce(sku, "schvalit");
export const zrusitSchvaleniRisk = (sku: string): Promise<void> =>
  riskAkce(sku, "zrusit-schvaleni");
export const skrytRisk = (sku: string): Promise<void> => riskAkce(sku, "skryt");

// ─── AZ PNEU ────────────────────────────────────────────────────────────────
// 🔑 Seznam se vrací SESKUPENÝ PO DEZÉNECH — jeden dezén = jeden budoucí
// produkt na eshopu, rozměry jsou jeho varianty.

export interface AzpneuRozmer {
  azpneuId: string;
  sizeLabel: string;
  position: string | null;
  manufacturerCode: string | null;
  /** VOC bez DPH — co reálně platíme dodavateli. */
  nakupka: string | null;
  /** Za kolik prodává az-pneu koncovému zákazníkovi (s DPH). ⛔ Ne naše cena. */
  dodavatelProdava: string | null;
  /** Co půjde na eshop — ruční přepis už je v tom. */
  naseCena: string | null;
  naseCenaNavrzena: string | null;
  cenaRucne: boolean;
  /** >0 = jsme nad dodavatelem. Nad ~0,2 je to podezřelé (chyba v jeho ceníku). */
  odchylkaProtiDodavateli: number | null;
  sklad: {
    opava: number; straz: number; do24h: number; do48h: number;
    do2az5d: number; celkem: number;
  };
  stav: "mimo" | "ceka" | "chyba" | "na-eshopu" | "skryto";
}

export interface AzpneuDezen {
  brand: string;
  pattern: string;
  kategorie: string | null;
  imgUrl: string | null;
  popis: string | null;
  /** Cenové rozpětí přes varianty — na kartě stačí „od–do". */
  cenaOd: number | null;
  cenaDo: number | null;
  skladCelkem: number;
  pocetRozmeru: number;
  naEshopu: number;
  rozmery: AzpneuRozmer[];
}

export async function listAzpneu(params: {
  kategorie?: string; znacka?: string; jenSkladem?: boolean; hledat?: string;
}): Promise<{ dezeny: AzpneuDezen[] }> {
  const qs = new URLSearchParams();
  if (params.kategorie) qs.set("kategorie", params.kategorie);
  if (params.znacka) qs.set("znacka", params.znacka);
  if (params.jenSkladem) qs.set("jenSkladem", "1");
  if (params.hledat) qs.set("hledat", params.hledat);
  return fetchJson(`/catalog/azpneu?${qs}`);
}

export async function getAzpneuStats(): Promise<{
  kategorie: Array<{ kategorie: string; pocet: number }>;
  naEshopu: number; ceka: number; bezKodu: number;
}> {
  return fetchJson(`/catalog/azpneu/stats`);
}

async function azpneuAkce(id: string, akce: "schvalit" | "skryt"): Promise<void> {
  const res = await fetch(`${BASE}/catalog/azpneu/${encodeURIComponent(id)}/${akce}`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    throw new Error(body.detail ?? body.error ?? `HTTP ${res.status}`);
  }
}

export const schvalitAzpneu = (id: string): Promise<void> => azpneuAkce(id, "schvalit");
export const skrytAzpneu = (id: string): Promise<void> => azpneuAkce(id, "skryt");

/** Ruční přepis ceny. `null` = zrušit a vrátit se k návrhu. */
export async function nastavCenuAzpneu(id: string, cena: number | null): Promise<void> {
  const res = await fetch(`${BASE}/catalog/azpneu/${encodeURIComponent(id)}/cena`, {
    method: "PATCH",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ cena }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    throw new Error(body.detail ?? body.error ?? `HTTP ${res.status}`);
  }
}

/** Detail DEZÉNU = budoucího produktu na eshopu (všechny jeho rozměry). */
export interface AzpneuDezenDetail {
  brand: string;
  pattern: string;
  kategorie: string | null;
  imgUrl: string | null;
  popisHtml: string | null;
  construction: string | null;
  usageTagRaw: string | null;
  naEshopu: number;
  rozmery: Array<AzpneuRozmer & { siIndex: string | null; velkoobchodUrl: string }>;
  vzorec: { dopravaKs: number; marzeMin: number; dph: number };
}

export async function getAzpneuDezen(brand: string, pattern: string): Promise<AzpneuDezenDetail> {
  return fetchJson(
    `/catalog/azpneu/dezen/${encodeURIComponent(brand)}/${encodeURIComponent(pattern)}`,
  );
}

// ─── ADRENALIN-OIL (FunnelWeb Filter + ACTION POWER) ────────────────────────

export interface AdrenalinPolozka {
  supplierId: string;
  supplierUrl: string | null;
  kod: string | null;
  znacka: string;
  druh: "filtr" | "riditka";
  nazev: string;
  cena: string | null;
  cenaDodavatele: string | null;
  cenaPrepsana: boolean;
  skladDodavatele: number;
  skladVice: boolean;
  ohyby: Array<{ kod: string; ohyb: string; ks: number;
    sirka?: number; vyska?: number; drop?: number; roztec?: number }>;
  fitmentModely: string[];
  fitmentRoky: string[];
  ktm: boolean;
  fotky: string[];
  eshopStav: "mimo" | "ceka" | "chyba" | "na-eshopu" | "skryto";
  shopifyHandle: string | null;
  shopifyStatus: string | null;
  chyba: string | null;
  status: string;
}

export async function listAdrenalin(params: {
  druh?: string; jenKtm?: boolean; q?: string; znacka?: string; jenSkladem?: boolean;
} = {}): Promise<{ pocet: number; polozky: AdrenalinPolozka[] }> {
  const qs = new URLSearchParams();
  if (params.druh) qs.set("druh", params.druh);
  if (params.jenKtm) qs.set("jenKtm", "1");
  if (params.q) qs.set("q", params.q);
  if (params.znacka) qs.set("znacka", params.znacka);
  if (params.jenSkladem) qs.set("jenSkladem", "1");
  return fetchJson(`/catalog/adrenalin?${qs}`);
}

export async function getAdrenalinPocty(): Promise<{
  vse: number; filtr: number; riditka: number; skladem: number;
  znacky: Array<{ znacka: string; pocet: number }>;
}> {
  return fetchJson(`/catalog/adrenalin/pocty`);
}

/** Detail = budoucí/živá karta na eshopu se vším, co o ní víme. */
export interface AdrenalinDetail extends AdrenalinPolozka {
  perexHtml: string | null;
  descriptionHtml: string | null;
  cenaPrepsanaKym: string | null;
  cenaPrepsanaKdy: string | null;
}

export async function getAdrenalin(supplierId: string): Promise<AdrenalinDetail> {
  return fetchJson(`/catalog/adrenalin/${encodeURIComponent(supplierId)}`);
}

/** Skrýt JEDEN ohyb řídítek. ⛔ Poslední variantu Shopify smazat nedovolí. */
export async function skrytAdrenalinOhyb(id: string, ohyb: string): Promise<void> {
  const res = await fetch(
    `${BASE}/catalog/adrenalin/${encodeURIComponent(id)}/skryt?ohyb=${encodeURIComponent(ohyb)}`,
    { method: "POST", credentials: "include", headers: { Accept: "application/json" } },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    throw new Error(body.detail ?? body.error ?? `HTTP ${res.status}`);
  }
}

export async function getAdrenalinStats(): Promise<{
  vedeDodavatel: number; vRozsahu: number; naEshopu: number; mohliBychom: number;
  ceka: number; chyby: number; filtru: number; riditek: number;
}> {
  return fetchJson(`/catalog/adrenalin/stats`);
}

async function adrenalinAkce(
  id: string,
  akce: "schvalit" | "skryt" | "zrusit-schvaleni",
): Promise<void> {
  const res = await fetch(`${BASE}/catalog/adrenalin/${encodeURIComponent(id)}/${akce}`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    throw new Error(body.detail ?? body.error ?? `HTTP ${res.status}`);
  }
}

export const schvalitAdrenalin = (id: string): Promise<void> => adrenalinAkce(id, "schvalit");
export const skrytAdrenalin = (id: string): Promise<void> => adrenalinAkce(id, "skryt");
export const zrusitSchvaleniAdrenalin = (id: string): Promise<void> =>
  adrenalinAkce(id, "zrusit-schvaleni");

/** Ruční přepis ceny. `null` = vrátit se k pultovce dodavatele. */
export async function nastavCenuAdrenalin(id: string, cena: number | null): Promise<void> {
  const res = await fetch(`${BASE}/catalog/adrenalin/${encodeURIComponent(id)}/cena`, {
    method: "PATCH",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ cena }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    throw new Error(body.detail ?? body.error ?? `HTTP ${res.status}`);
  }
}

// ───────────────────────────── ÖHLINS rozkresy ─────────────────────────────

export type OhlinsRozkresStrana = {
  id: string;
  strana: number;
  nadpis: string | null;
  sirka: number;
  vyska: number;
  pdfCesta: string;
  polozek: number;
  bodu: number;
};

export type OhlinsRozkresDetail = {
  id: string;
  produktKod: string;
  strana: number;
  nadpis: string | null;
  sirka: number;
  vyska: number;
  /** Vektorové SVG celé strany (~350 kB). */
  svg: string;
  polozky: Array<{ poz: string; dilKod: string; ks: number; popis: string | null }>;
  /** ⚠ Jedna `poz` může mít víc bodů — symetrické díly mají dva vývody. */
  body: Array<{
    poz: string; dilKod: string; figura: number;
    x: number; y: number; s: number; v: number;
  }>;
};

export async function ohlinsRozkresy(): Promise<{
  produkty: Array<{ produktKod: string; stran: number }>;
  celkem: { produktu: number; stran: number; bodu: number };
}> {
  return fetchJson(`/catalog/ohlins/rozkresy`);
}

export async function ohlinsRozkresyProduktu(produktKod: string): Promise<{
  produktKod: string;
  strany: OhlinsRozkresStrana[];
}> {
  return fetchJson(`/catalog/ohlins/rozkresy/${encodeURIComponent(produktKod)}`);
}

export async function ohlinsRozkres(id: string): Promise<OhlinsRozkresDetail> {
  return fetchJson(`/catalog/ohlins/rozkres/${encodeURIComponent(id)}`);
}

// ───────────────────────── SG12.cz — GAERNE Cross / Enduro ─────────────────

/**
 * 🔑 Jeden REF = jedna budoucí karta na eshopu, velikosti jsou její varianty.
 * 🔴 `naKarte` × `vTabulce` jsou DVA zdroje dovozce, které se rozcházejí
 *    u 68 % karet — ⛔ neslučovat, katalog rozpor přiznává.
 */
export interface Sg12Karta {
  ref: string;
  rada: string;
  barva: string | null;
  /** Doplněk na MX botu (pásek, podrážka, vložka…) — nemá velikosti. */
  jePrislusenstvi: boolean;
  nazev: string;
  nazevPuvodni: string;
  fotka: string | null;
  pocetFotek: number;
  maPopis: boolean;
  cenikovaDovozce: string | null;
  prodavaDovozceZa: string | null;
  naseCena: string | null;
  naseCenaNavrzena: string | null;
  cenaRucne: boolean;
  vAkci: boolean;
  slevaPct: number | null;
  cenaZmenaKdy: string | null;
  odchylkaProtiDovozci: number | null;
  vedemeVAbre: boolean;
  skladVlasim: number;
  velikostiCelkem: number;
  velikostiUDovozce: number;
  rozporVelikosti: number;
  stav: "mimo" | "ceka" | "chyba" | "na-eshopu" | "skryto";
}

export interface Sg12Velikost {
  velikost: string;
  naKarte: boolean;
  vTabulce: boolean;
  abraKod: string | null;
  abraCenaBezDph: string | null;
  skladVlasim: number;
}

export interface Sg12Detail extends Omit<Sg12Karta, "fotka" | "pocetFotek" | "maPopis"> {
  rodina: string;
  /** Kód karty v ABŘE pro celý produkt — jen u příslušenství. */
  abraKod: string | null;
  popisHtml: string | null;
  fotky: string[];
  odkazDovozce: string;
  kategorieDovozce: string[];
  mocZAbry: string | null;
  cenuPrepsal: string | null;
  cenuPrepsalKdy: string | null;
  shopifyHandle: string | null;
  shopifyTitle: string | null;
  foundedError: string | null;
  velikosti: Sg12Velikost[];
  historieCen: Array<{
    kdy: string;
    cenikova: string | null;
    prodejni: string | null;
    vAkci: boolean;
    rozdil: string | null;
  }>;
}

export async function listSg12(params: {
  rada?: string; jenAkce?: boolean; jenVedeme?: boolean; hledat?: string;
}): Promise<{ karty: Sg12Karta[] }> {
  const qs = new URLSearchParams();
  if (params.rada) qs.set("rada", params.rada);
  if (params.jenAkce) qs.set("jenAkce", "1");
  if (params.jenVedeme) qs.set("jenVedeme", "1");
  if (params.hledat) qs.set("hledat", params.hledat);
  return fetchJson(`/catalog/sg12?${qs}`);
}

export async function getSg12Stats(): Promise<{
  rady: Array<{ rada: string; pocet: number }>;
  naEshopu: number;
  /** Kolik karet má u dovozce přeškrtnutou cenu — ⚠ je to VŠECHNO (66/66). */
  vAkci: number;
  ceka: number;
  /** Sleva 30 % a víc — teprve tohle je signál. */
  vyraznaSleva: number;
  vedeme: number;
  posledniBeh: {
    kdy: string; stav: string; karet: number;
    zmenaCeny: number; zmenaAkce: number; rozporVelikosti: number;
  } | null;
}> {
  return fetchJson(`/catalog/sg12/stats`);
}

export async function getSg12(ref: string): Promise<Sg12Detail> {
  return fetchJson(`/catalog/sg12/${encodeURIComponent(ref)}`);
}

/** Co se u dovozce pohnulo s cenou — zadání Víta „hlídat jejich ceny, akce". */
export async function getSg12ZmenyCen(dnu = 30): Promise<{
  dnu: number;
  zmeny: Array<{
    ref: string; nazev: string; rada: string; fotka: string | null;
    kdy: string; cena: string | null; cenikova: string | null;
    rozdil: string | null; vAkci: boolean;
  }>;
}> {
  return fetchJson(`/catalog/sg12/zmeny-cen?dnu=${dnu}`);
}

async function sg12Akce(ref: string, akce: "schvalit" | "skryt"): Promise<void> {
  const res = await fetch(`${BASE}/catalog/sg12/${encodeURIComponent(ref)}/${akce}`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    throw new Error(body.detail ?? body.error ?? `HTTP ${res.status}`);
  }
}

export const schvalitSg12 = (ref: string): Promise<void> => sg12Akce(ref, "schvalit");
export const skrytSg12 = (ref: string): Promise<void> => sg12Akce(ref, "skryt");

/** Ruční přepis ceny. `null` = vrátit se k návrhu. */
export async function nastavCenuSg12(ref: string, cena: number | null): Promise<void> {
  const res = await fetch(`${BASE}/catalog/sg12/${encodeURIComponent(ref)}/cena`, {
    method: "PATCH",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ cena }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    throw new Error(body.detail ?? body.error ?? `HTTP ${res.status}`);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
//  ANGEL'S WHEELS (angelswheels.com) — kola Kite Elite / G2 ProSeries + díly
// ─────────────────────────────────────────────────────────────────────────────
// 🔑 Dva druhy položek: KOLO (4 karty, varianty = cenové body kolo × ráfek
//    z konfigurátoru dodavatele) a DÍL (varianty = barva).
// 💰 Cena = cena dodavatele 1 : 1 (Vít 8. 9. 2026); ruční přepis vyhrává.

export type AwStav = "mimo" | "ceka" | "chyba" | "na-eshopu" | "skryto";

export interface AwBarva { id: string; hex: string; nazev: string }
export interface AwBodKola {
  kolo: "predni" | "zadni" | "sada";
  koloNazev: string;
  rimId: string;
  rafek: string;
  sku: string;
  cenaDodavatele: string | null;
  naseCena: string | null;
  cenaRucne: boolean;
  /** Konfigurátor tuhle kombinaci opravdu dal (náboj se neprepnul na jiný). */
  dostupne: boolean;
  cenaZmenaKdy: string | null;
  shopifyVariantId: string | null;
}
export interface AwKolo {
  key: string;
  nazev: string;
  naboj: "kite" | "g2";
  /** Co se kupuje — karta = kolo × náboj (Vít 8. 9. 2026). */
  kolo: "predni" | "zadni" | "sada";
  fitment: "ktm" | "sx" | "exc";
  model: string;
  predni: string;
  zadni: string;
  barvy: { hub?: AwBarva[]; nipple?: AwBarva[]; spokes?: AwBarva[] };
  rafky: Array<{ id: string; name: string }>;
  body: AwBodKola[];
  stav: AwStav;
  shopifyHandle: string | null;
  foundedError: string | null;
  cenaZmenaKdy: string | null;
}
export interface AwKoloDetail extends AwKolo {
  popisHtml: string | null;
  shopifyTitle: string | null;
  body: Array<AwBodKola & { historie: Array<{ kdy: string; cena: string | null; rozdil: string | null }> }>;
}
export interface AwDil {
  ref: string;
  police: string;
  policeNazev: string;
  nazev: string;
  nazevPuvodni: string;
  fotka: string | null;
  pocetFotek: number;
  maPopis: boolean;
  kategorieDodavatele: string;
  ktmFit: boolean;
  univerzal: boolean;
  fitBrands: string[];
  cenaDodavatele: string | null;
  naseCena: string | null;
  cenaRucne: boolean;
  cenaZmenaKdy: string | null;
  skladDodavatele: string;
  vedemeVAbre: boolean;
  skladVlasim: number;
  varianty: Array<{ label: string; sku: string; cena: string | null; sklad: number }>;
  stav: AwStav;
}
export interface AwDilDetail extends Omit<AwDil, "fotka" | "pocetFotek" | "maPopis" | "varianty"> {
  popisHtml: string | null;
  fotky: string[];
  odkazDodavatele: string;
  naseCenaNavrzena: string | null;
  cenuPrepsal: string | null;
  cenuPrepsalKdy: string | null;
  abraKod: string | null;
  abraCenaBezDph: string | null;
  shopifyHandle: string | null;
  shopifyTitle: string | null;
  foundedError: string | null;
  varianty: Array<{ label: string; paramName: string | null; sku: string; cena: string | null; sklad: number; shopifyVariantId: string | null }>;
  historieCen: Array<{ kdy: string; cena: string | null; rozdil: string | null }>;
}

export async function getAwStats(): Promise<{
  police: Array<{ key: string; nazev: string; pocet: number }>;
  kola: { celkem: number; naEshopu: number; ceka: number; bodu: number };
  dily: { celkem: number; naEshopu: number; ceka: number; vedeme: number };
  posledniBeh: { kdy: string; stav: string; dilu: number; kolaBodu: number; zmenaCeny: number; ciziZnacky: number; anonymniOk: boolean } | null;
}> {
  return fetchJson(`/catalog/angelswheels/stats`);
}
export async function listAwKola(): Promise<{ kola: AwKolo[] }> {
  return fetchJson(`/catalog/angelswheels/kola`);
}
export async function getAwKolo(key: string): Promise<AwKoloDetail> {
  return fetchJson(`/catalog/angelswheels/kola/${encodeURIComponent(key)}`);
}
export async function listAwDily(params: { police?: string; hledat?: string; jenVedeme?: boolean }): Promise<{ dily: AwDil[] }> {
  const qs = new URLSearchParams();
  if (params.police) qs.set("police", params.police);
  if (params.hledat) qs.set("hledat", params.hledat);
  if (params.jenVedeme) qs.set("jenVedeme", "1");
  return fetchJson(`/catalog/angelswheels/dily?${qs}`);
}
export async function getAwDil(ref: string): Promise<AwDilDetail> {
  return fetchJson(`/catalog/angelswheels/dily/${encodeURIComponent(ref)}`);
}
export async function getAwZmenyCen(dnu = 30): Promise<{
  dnu: number;
  zmeny: Array<{ kdy: string; cena: string | null; rozdil: string | null; ref: string; druh: "dil" | "kolo"; nazev: string; odkaz: string }>;
}> {
  return fetchJson(`/catalog/angelswheels/zmeny-cen?dnu=${dnu}`);
}

async function awPost(cesta: string, body?: unknown, method = "POST"): Promise<void> {
  const res = await fetch(`${BASE}/catalog/angelswheels/${cesta}`, {
    method,
    credentials: "include",
    headers: { Accept: "application/json", ...(body !== undefined ? { "Content-Type": "application/json" } : {}) },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    const b = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    throw new Error(b.detail ?? b.error ?? `HTTP ${res.status}`);
  }
}
export const schvalitAwKolo = (key: string): Promise<void> => awPost(`kola/${encodeURIComponent(key)}/schvalit`);
export const skrytAwKolo = (key: string): Promise<void> => awPost(`kola/${encodeURIComponent(key)}/skryt`);
export const schvalitAwDil = (ref: string): Promise<void> => awPost(`dily/${encodeURIComponent(ref)}/schvalit`);
export const skrytAwDil = (ref: string): Promise<void> => awPost(`dily/${encodeURIComponent(ref)}/skryt`);
/** Ruční cena jednoho cenového bodu kola. `null` = zpět na cenu dodavatele. */
export const nastavCenuAwKolo = (key: string, sku: string, cena: number | null): Promise<void> =>
  awPost(`kola/${encodeURIComponent(key)}/cena`, { sku, cena }, "PATCH");
export const nastavCenuAwDil = (ref: string, cena: number | null): Promise<void> =>
  awPost(`dily/${encodeURIComponent(ref)}/cena`, { cena }, "PATCH");
